import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, DollarSign, TrendingUp, TrendingDown, ScrollText, FileText, Landmark, Banknote, ArrowUpRight, ExternalLink, Clock, AlertTriangle, Download, Undo2, Receipt, CheckCircle2, XCircle, Equal, Eye, Trash2, RefreshCcw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ExportButton from "@/components/ExportButton";
import RetencionesRecibidasControl from "@/components/RetencionesRecibidasControl";
import DetalleCuentaDialog from "@/components/DetalleCuentaDialog";

const METODOS = [
  { value: "efectivo", label: "Efectivo", icon: Banknote },
  { value: "cheque", label: "Cheque", icon: FileText },
  { value: "transferencia", label: "Transferencia", icon: Landmark },
  { value: "tarjeta", label: "Tarjeta", icon: ArrowUpRight },
  { value: "nota_credito", label: "Nota de crédito", icon: FileText },
  { value: "otro", label: "Otro", icon: FileText },
];

export default function Accounting() {
  const [resumen, setResumen] = useState({});
  const [retSync, setRetSync] = useState({ count: 0, saldo_inflado_total: 0, ejemplos: [] });
  const [syncing, setSyncing] = useState(false);
  const loadRetSync = () => api.get("/accounting/cuentas-cobrar/retenciones-desincronizadas/count").then(r => setRetSync(r.data)).catch(() => {});
  useEffect(() => { api.get("/accounting/resumen").then(r => setResumen(r.data)).catch(() => {}); loadRetSync(); }, []);

  const sincronizarTodas = async () => {
    if (!window.confirm(`Se sincronizarán ${retSync.count} CxC con retenciones registradas pero no propagadas. Esto recalcula saldo y estado en la BD. ¿Continuar?`)) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/accounting/cuentas-por-cobrar/sincronizar-retenciones?aplicar=true");
      toast.success(`${data.cxc_actualizadas} CxC sincronizadas`);
      loadRetSync();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSyncing(false); }
  };

  return (
    <div className="space-y-6" data-testid="accounting-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Contabilidad</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Plan de cuentas, cartera, cobros, pagos y asientos contables.</p>
      </div>
      {retSync.count > 0 && (
        <div
          className="rounded-md border border-[#e8b98a] bg-[#fef4e6] px-4 py-3 flex items-start gap-3"
          data-testid="ret-desync-banner"
        >
          <AlertTriangle size={20} className="text-[#a35400] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[#7a3f00]">
              {retSync.count} CxC con retenciones sin sincronizar — cartera está inflada ${retSync.saldo_inflado_total.toFixed(2)}
            </div>
            <div className="text-xs text-[#8a5500] mt-1">
              Hay comprobantes de retención registrados que no descuentan el saldo de las CxC. Sincroniza para corregir la deuda mostrada al cliente y en Cartera.
              {retSync.ejemplos.length > 0 && (
                <div className="mt-1 space-x-2">
                  <span className="text-[#5c7368]">Ejemplos:</span>
                  {retSync.ejemplos.slice(0, 3).map(e => (
                    <span key={e.cxc_id} className="inline-block bg-white/60 rounded px-1.5 py-0.5 text-[10px]">
                      {e.cliente_nombre} · #{e.numero_factura} · +${e.diferencia.toFixed(2)}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <Button
            size="sm"
            onClick={sincronizarTodas}
            disabled={syncing}
            className="bg-[#a35400] hover:bg-[#7a3f00] text-white shrink-0"
            data-testid="ret-desync-fix-btn"
          >
            <RefreshCcw size={12} className={`mr-1 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Sincronizando…" : "Reparar todo"}
          </Button>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={TrendingUp} label="CxC total" value={`$${(resumen.cxc_total ?? 0).toFixed(2)}`} tone="primary" />
        <StatCard icon={TrendingUp} label="CxC vencidas" value={`$${(resumen.cxc_vencidas ?? 0).toFixed(2)}`} tone="danger" />
        <StatCard icon={TrendingDown} label="CxP total" value={`$${(resumen.cxp_total ?? 0).toFixed(2)}`} tone="warning" />
        <StatCard icon={TrendingDown} label="CxP vencidas" value={`$${(resumen.cxp_vencidas ?? 0).toFixed(2)}`} tone="danger" />
      </div>

      <Tabs defaultValue="cxc">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="cxc">CxC</TabsTrigger>
          <TabsTrigger value="cxp">CxP</TabsTrigger>
          <TabsTrigger value="aging" data-testid="aging-tab"><Clock size={14} className="mr-2" />Antigüedad</TabsTrigger>
          <TabsTrigger value="cobros">Cobros</TabsTrigger>
          <TabsTrigger value="pagos">Pagos</TabsTrigger>
          <TabsTrigger value="retenciones" data-testid="retenciones-tab"><Receipt size={14} className="mr-2" />Retenciones</TabsTrigger>
          <TabsTrigger value="cuentas">Plan de cuentas</TabsTrigger>
          <TabsTrigger value="asientos"><ScrollText size={14} className="mr-2" />Asientos</TabsTrigger>
        </TabsList>
        <TabsContent value="cxc"><CxcList /></TabsContent>
        <TabsContent value="cxp"><CxpList /></TabsContent>
        <TabsContent value="aging"><AgingPanel /></TabsContent>
        <TabsContent value="cobros"><Cobros /></TabsContent>
        <TabsContent value="pagos"><Pagos /></TabsContent>
        <TabsContent value="retenciones"><RetencionesRecibidasControl /></TabsContent>
        <TabsContent value="cuentas"><PlanCuentas /></TabsContent>
        <TabsContent value="asientos"><Asientos /></TabsContent>
      </Tabs>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone = "default" }) {
  const tones = {
    default: "bg-white border-[#d1dbd6]",
    warning: "bg-[#fef7ec] border-[#f5c477]",
    danger: "bg-[#fdecea] border-[#d99584]",
    primary: "bg-[#e9f2ee] border-[#b2d0c2]",
  };
  return (
    <div className={`rounded-md border p-5 ${tones[tone]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#5c7368]">{label}</div>
        <Icon size={16} className="text-[#205c45]" />
      </div>
      <div className="text-2xl font-bold tabular-nums" style={{ fontFamily: "'Manrope', sans-serif" }}>{value}</div>
    </div>
  );
}

function CxcList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sel, setSel] = useState([]); // array de {cxc, monto}
  const [form, setForm] = useState(emptyCobro());

  function emptyCobro() {
    return {
      fecha: new Date().toISOString().slice(0, 10),
      metodo: "efectivo",
      banco: "", numero_cheque: "", numero_transaccion: "",
      titular_cheque: "", valor_cheque: "",
      numero_recibo: "",
      observaciones: "",
    };
  }

  // Paginación + búsqueda servidor (Abr 2026 — soporta miles de CxC)
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [sortBy, setSortBy] = useState("fecha_factura");
  const [sortDir, setSortDir] = useState("desc"); // asc | desc
  const [editingCxc, setEditingCxc] = useState(null);
  const [detalleCxc, setDetalleCxc] = useState(null); // CxC con cobros/retenciones para "Ver detalle"

  const openDetalle = async (c) => {
    setDetalleCxc({ loading: true, id: c.id });
    try {
      const { data } = await api.get(`/accounting/cuentas/${c.id}/detalle`);
      setDetalleCxc(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetalleCxc(null);
    }
  };

  const deleteCxc = async (c) => {
    if (!window.confirm(`¿Eliminar la CxC de "${c.cliente_nombre}" · factura ${c.numero_factura} · $${(+c.monto_total).toFixed(2)}?\n\nSolo se permite si NO tiene cobros aplicados (no anulados).`)) return;
    try {
      await api.delete(`/accounting/cuentas-cobrar/${c.id}`);
      toast.success(`CxC ${c.numero_factura} eliminada`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEditCxc = async () => {
    if (!editingCxc) return;
    try {
      await api.patch(`/accounting/cuentas-por-cobrar/${editingCxc.id}`, {
        numero_factura: editingCxc.numero_factura || null,
        fecha_factura: editingCxc.fecha_factura || null,
        fecha_vencimiento: editingCxc.fecha_vencimiento || null,
        monto_total: Number(editingCxc.monto_total) || 0,
        retencion_aplicada: editingCxc.retencion_aplicada != null ? Number(editingCxc.retencion_aplicada) : null,
        nc_aplicada_valor: editingCxc.nc_aplicada_valor != null ? Number(editingCxc.nc_aplicada_valor) : null,
        cliente_nombre: editingCxc.cliente_nombre || null,
        observaciones: editingCxc.observaciones || null,
      });
      toast.success("CxC actualizada");
      setEditingCxc(null);
      load().catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const load = async () => {
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (estado) params.estado = estado;
    if (q) params.q = q;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (sortBy) params.sort_by = sortBy;
    if (sortDir) params.sort_dir = sortDir;
    const r = await api.get("/accounting/cuentas-cobrar", { params });
    setItems(r.data || []);
    setTotal(parseInt(r.headers?.["x-total-count"] || r.data?.length || 0, 10));
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); /* eslint-disable-next-line */ }, [page, estado, q, desde, hasta, sortBy, sortDir]);
  useEffect(() => { setPage(0); }, [q, estado, desde, hasta]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };
  const sortIcon = (col) => sortBy !== col ? "" : (sortDir === "asc" ? " ▲" : " ▼");

  const filteredItems = items;
  const pagedItems = items;

  const [facturasCliente, setFacturasCliente] = useState([]);
  const openCobrar = async (c) => {
    setSel([{ id: c.id, cxc: c, monto: c.saldo.toFixed(2) }]);
    setForm(emptyCobro());
    setDialogOpen(true);
    // Cargar TODAS las CxC pendientes/parciales del mismo cliente (todas las páginas)
    try {
      const [pend, parc] = await Promise.all([
        api.get("/accounting/cuentas-cobrar", { params: { estado: "pendiente", limit: 5000 } }),
        api.get("/accounting/cuentas-cobrar", { params: { estado: "parcial", limit: 5000 } }),
      ]);
      const all = [...(pend.data || []), ...(parc.data || [])]
        .filter(i => i.cliente_id === c.cliente_id && i.saldo > 0);
      const seen = new Set();
      setFacturasCliente(all.filter(x => seen.has(x.id) ? false : (seen.add(x.id), true)));
    } catch (_) { setFacturasCliente([]); }
  };

  const addFactura = (c) => {
    if (sel.find(s => s.id === c.id)) return;
    setSel(s => [...s, { id: c.id, cxc: c, monto: c.saldo.toFixed(2) }]);
  };

  const removeFactura = (id) => setSel(s => s.filter(x => x.id !== id));
  const updateMonto = (id, v) => setSel(s => s.map(x => x.id === id ? { ...x, monto: v } : x));

  // Acción admin: reabrir CxC marcada como pagada sin cobro real asociado
  const reabrirCxc = async (c) => {
    const numero = c.numero_documento || c.id.slice(0, 8);
    if (!window.confirm(
      `¿Reabrir la CxC ${numero} de ${c.cliente_nombre}?\n\n` +
      `Monto: $${c.monto_total?.toFixed(2)}\n` +
      `Saldo actual: $${c.saldo?.toFixed(2)} (PAGADA)\n\n` +
      `Si NO tiene cobros reales asociados, volverá a estado PENDIENTE para que puedas registrar el cobro.\n` +
      `Si SÍ tiene cobros, debes anularlos primero desde la pestaña "Cobros".`
    )) return;
    try {
      const { data } = await api.post(`/accounting/cuentas/${c.id}/reabrir`);
      toast.success(`CxC ${data.numero_documento || ""} reabierta · saldo $${data.saldo_nuevo.toFixed(2)}`);
      load().catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  const montoTotal = useMemo(
    () => sel.reduce((t, s) => t + (parseFloat(s.monto) || 0), 0),
    [sel]
  );

  const mismoCliente = useMemo(
    () => sel.length > 0 && sel.every(s => s.cxc.cliente_id === sel[0].cxc.cliente_id),
    [sel]
  );

  const facturasMismoCliente = useMemo(() => {
    if (sel.length === 0) return [];
    const cid = sel[0].cxc.cliente_id;
    const fuente = facturasCliente.length > 0 ? facturasCliente : items;
    return fuente.filter(i => i.cliente_id === cid && i.saldo > 0 && !sel.find(s => s.id === i.id));
  }, [sel, items, facturasCliente]);

  const cobrar = async () => {
    if (sel.length === 0) return toast.error("Agregue al menos una factura");
    if (!mismoCliente) return toast.error("Todas las facturas deben ser del mismo cliente");
    if (form.metodo === "cheque" && (!form.banco || !form.numero_cheque)) {
      return toast.error("Cheque requiere banco y número");
    }
    if (form.metodo === "transferencia" && (!form.banco || !form.numero_transaccion)) {
      return toast.error("Transferencia requiere banco y número");
    }
    if (form.metodo === "nota_credito" && !form.nota_credito_id) {
      return toast.error("Seleccione la nota de crédito a aplicar");
    }
    try {
      await api.post("/accounting/cobros", {
        fecha: form.fecha,
        metodo: form.metodo,
        aplicaciones: sel.map(s => ({ cuenta_por_cobrar_id: s.id, monto: parseFloat(s.monto) || 0 })),
        banco: form.banco || null,
        numero_cheque: form.numero_cheque || null,
        numero_transaccion: form.numero_transaccion || null,
        titular_cheque: form.titular_cheque || null,
        valor_cheque: form.valor_cheque ? parseFloat(form.valor_cheque) : null,
        nota_credito_id: form.nota_credito_id || null,
        numero_recibo: form.numero_recibo || null,
        observaciones: form.observaciones || null,
      });
      toast.success(`Cobro registrado · ${sel.length} factura${sel.length > 1 ? "s" : ""} · Total $${montoTotal.toFixed(2)}`);
      setDialogOpen(false); setSel([]); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cols = [
    { label: "Documento", accessor: "numero_documento" },
    { label: "Cliente", accessor: "cliente_nombre" },
    { label: "Factura", accessor: "numero_factura" },
    { label: "N° Recibo", accessor: "numero_recibo" },
    { label: "Fecha", accessor: "fecha_factura" },
    { label: "Vencimiento", accessor: "fecha_vencimiento" },
    { label: "Total", accessor: (r) => `$${r.monto_total?.toFixed(2)}` },
    { label: "Pagado", accessor: (r) => `$${r.monto_pagado?.toFixed(2)}` },
    { label: "Retención", accessor: (r) => `$${(r.retencion_aplicada || 0).toFixed(2)}` },
    { label: "Saldo", accessor: (r) => `$${r.saldo?.toFixed(2)}` },
    { label: "Estado", accessor: "estado" },
  ];

  const sincronizarRetenciones = async () => {
    if (!window.confirm("Sincronizar retenciones recibidas contra CxC. Esto recalcula saldo y estado en TODAS las CxC que tengan comprobantes de retención registrados. ¿Continuar?")) return;
    try {
      const { data } = await api.post("/accounting/cuentas-por-cobrar/sincronizar-retenciones?aplicar=true");
      toast.success(`Retenciones sincronizadas: ${data.cxc_actualizadas} CxC actualizadas (de ${data.cxc_con_discrepancia} con discrepancia)`);
      load().catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[260px]">
          <Input placeholder="Buscar cliente o número factura..." value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="cxc-search" />
        </div>
        <select value={estado} onChange={(e) => setEstado(e.target.value)} className="h-9 px-2 rounded border border-[#d1dbd6] text-sm bg-white" data-testid="cxc-estado">
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="parcial">Parcial</option>
          <option value="pagada">Pagada</option>
          <option value="vencida">Vencida</option>
        </select>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-36" placeholder="Desde" data-testid="cxc-desde" title="Fecha desde" />
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-36" placeholder="Hasta" data-testid="cxc-hasta" title="Fecha hasta" />
        {(desde || hasta || q || estado) && (
          <Button size="sm" variant="ghost" onClick={() => { setQ(""); setEstado(""); setDesde(""); setHasta(""); }} className="text-[#b53a25]" data-testid="cxc-clear">Limpiar</Button>
        )}
        <span className="text-xs text-[#5c7368] ml-2 self-center">{total} total{total !== 1 ? "es" : ""}</span>
        <ExportButton title="Cuentas por cobrar" columns={cols} rows={items} filename="cxc" />
        {isAdmin && (
          <Button
            size="sm"
            variant="outline"
            onClick={sincronizarRetenciones}
            className="text-[#205c45] border-[#205c45] hover:bg-[#e9f2ee]"
            data-testid="cxc-sync-retenciones-btn"
            title="Recalcula la retención y saldo de las CxC desde los comprobantes registrados (solo admin)"
          >
            <RefreshCcw size={12} className="mr-1" />Sincronizar retenciones
          </Button>
        )}
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr>
            <th onClick={() => toggleSort("numero_documento")} className="cursor-pointer select-none">Documento{sortIcon("numero_documento")}</th>
            <th onClick={() => toggleSort("cliente_nombre")} className="cursor-pointer select-none">Cliente{sortIcon("cliente_nombre")}</th>
            <th onClick={() => toggleSort("numero_factura")} className="cursor-pointer select-none">Factura{sortIcon("numero_factura")}</th>
            <th>N° Recibo</th>
            <th onClick={() => toggleSort("fecha_factura")} className="cursor-pointer select-none">Fecha{sortIcon("fecha_factura")}</th>
            <th onClick={() => toggleSort("fecha_vencimiento")} className="cursor-pointer select-none">Vencimiento{sortIcon("fecha_vencimiento")}</th>
            <th onClick={() => toggleSort("monto_total")} className="text-right cursor-pointer select-none">Total{sortIcon("monto_total")}</th>
            <th className="text-right">Pagado</th>
            <th className="text-right" title="Retención IR + IVA recibida del cliente (comprobante SRI)">Retención</th>
            <th onClick={() => toggleSort("saldo")} className="text-right cursor-pointer select-none">Saldo{sortIcon("saldo")}</th>
            <th onClick={() => toggleSort("estado")} className="cursor-pointer select-none">Estado{sortIcon("estado")}</th>
            <th className="w-28"></th>
          </tr></thead>
          <tbody>
            {pagedItems.map(c => (
              <tr key={c.id}>
                <td className="font-mono text-xs">
                  {c.factura_id ? (
                    <Link to={`/facturacion?f=${c.factura_id}`} className="text-[#205c45] hover:underline inline-flex items-center gap-1" title="Ver factura y productos vendidos">
                      {c.numero_documento || c.id.slice(0, 8)} <ExternalLink size={10} />
                    </Link>
                  ) : (c.numero_documento || c.id.slice(0, 8))}
                </td>
                <td className="font-medium">
                  <div>{c.cliente_nombre}</div>
                  {c.cliente_nombre_comercial && c.cliente_nombre_comercial !== c.cliente_nombre && (
                    <div className="text-[10px] text-[#5c7368] font-normal italic">{c.cliente_nombre_comercial}</div>
                  )}
                </td>
                <td className="text-xs font-mono">{c.numero_factura || "—"}</td>
                <td className="text-xs font-mono text-[#205c45]">{c.numero_recibo || "—"}</td>
                <td>{c.fecha_factura}</td>
                <td>{c.fecha_vencimiento}</td>
                <td className="text-right tabular-nums">${c.monto_total?.toFixed(2)}</td>
                <td className="text-right tabular-nums">${c.monto_pagado?.toFixed(2)}</td>
                <td
                  className={`text-right tabular-nums ${(c.retencion_aplicada || 0) > 0 ? "text-[#205c45] font-semibold" : "text-[#8ba397]"}`}
                  data-testid={`cxc-retencion-${c.id}`}
                  title={c.retencion_comprobantes && c.retencion_comprobantes.length ? `Comprobante(s): ${c.retencion_comprobantes.join(", ")}` : ""}
                >
                  {(c.retencion_aplicada || 0) > 0 ? `$${(+c.retencion_aplicada).toFixed(2)}` : "—"}
                  {c._retencion_sync_pendiente && (
                    <span className="ml-1 text-[9px] text-[#8a5500]" title="Retención registrada pero no propagada al CxC — usar 'Sincronizar retenciones' en Utilidades">⚠</span>
                  )}
                </td>
                <td className="text-right tabular-nums font-semibold">${c.saldo?.toFixed(2)}</td>
                <td><Badge variant="outline" className="capitalize">{c.estado}</Badge></td>
                <td>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openDetalle(c)} title="Ver detalle de cobros y retenciones" data-testid={`detalle-cxc-${c.id}`}>
                    <Eye size={14} className="text-[#205c45]" />
                  </Button>
                  {c.saldo > 0 && <Button size="sm" className="bg-[#205c45]" onClick={() => openCobrar(c)} data-testid={`cobrar-${c.id}`}>Cobrar</Button>}
                  {c.saldo <= 0.001 && isAdmin && c.estado === "pagada" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[#b08400] border-[#b08400] hover:bg-[#fef7e0]"
                      onClick={() => reabrirCxc(c)}
                      data-testid={`reabrir-cxc-${c.id}`}
                      title="Reabrir CxC (solo admin · solo si NO tiene cobros reales asociados)"
                    >
                      <Undo2 size={14} className="mr-1" /> Reabrir
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 ml-1"
                      onClick={() => setEditingCxc({ ...c })}
                      title="Editar CxC (solo admin · corregir monto/retención)"
                      data-testid={`edit-cxc-${c.id}`}
                    >
                      <FileText size={14} className="text-[#205c45]" />
                    </Button>
                  )}
                  {isAdmin && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 ml-1"
                      onClick={() => deleteCxc(c)}
                      title="Eliminar CxC duplicada (solo admin · debe estar sin cobros)"
                      data-testid={`del-cxc-${c.id}`}
                    >
                      <Trash2 size={14} className="text-[#b53a25]" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={12} className="text-center py-10 text-[#8ba397]">Sin CxC registradas</td></tr>}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex justify-center items-center gap-4 text-xs pb-12">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="cxc-page-prev">← Anterior</Button>
          <span className="text-[#5c7368]">Página {page + 1} de {Math.ceil(total / PAGE_SIZE)} · Mostrando {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)}</span>
          <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} data-testid="cxc-page-next">Siguiente →</Button>
        </div>
      )}

      <CobroPagoDialog
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        title="Registrar cobro"
        sel={sel} setSel={setSel} removeFactura={removeFactura} updateMonto={updateMonto}
        form={form} setForm={setForm} montoTotal={montoTotal}
        facturasMismoCliente={facturasMismoCliente} addFactura={addFactura}
        entityLabel="cliente" onSave={cobrar}
      />

      {/* ====== EDIT CxC (solo admin) ====== */}
      <Dialog open={!!editingCxc} onOpenChange={(v) => { if (!v) setEditingCxc(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar CxC {editingCxc?.numero_factura || ""}</DialogTitle>
          </DialogHeader>
          {editingCxc && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2">
                <Label>Cliente</Label>
                <Input value={editingCxc.cliente_nombre || ""} onChange={(e) => setEditingCxc({ ...editingCxc, cliente_nombre: e.target.value })} />
              </div>
              <div>
                <Label>N° Factura</Label>
                <Input value={editingCxc.numero_factura || ""} onChange={(e) => setEditingCxc({ ...editingCxc, numero_factura: e.target.value })} />
              </div>
              <div>
                <Label>Monto total ($)</Label>
                <Input type="number" step="0.01" value={editingCxc.monto_total ?? 0} onChange={(e) => setEditingCxc({ ...editingCxc, monto_total: e.target.value })} data-testid="cxc-monto-input" />
              </div>
              <div>
                <Label>Retención aplicada ($)</Label>
                <Input type="number" step="0.01" value={editingCxc.retencion_aplicada ?? 0} onChange={(e) => setEditingCxc({ ...editingCxc, retencion_aplicada: e.target.value })} data-testid="cxc-retencion-input" />
              </div>
              <div>
                <Label>NC aplicada ($)</Label>
                <Input type="number" step="0.01" value={editingCxc.nc_aplicada_valor ?? 0} onChange={(e) => setEditingCxc({ ...editingCxc, nc_aplicada_valor: e.target.value })} />
              </div>
              <div>
                <Label>Fecha factura</Label>
                <Input type="date" value={editingCxc.fecha_factura || ""} onChange={(e) => setEditingCxc({ ...editingCxc, fecha_factura: e.target.value })} />
              </div>
              <div>
                <Label>Fecha vencimiento</Label>
                <Input type="date" value={editingCxc.fecha_vencimiento || ""} onChange={(e) => setEditingCxc({ ...editingCxc, fecha_vencimiento: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Observaciones</Label>
                <Textarea rows={2} value={editingCxc.observaciones || ""} onChange={(e) => setEditingCxc({ ...editingCxc, observaciones: e.target.value })} />
              </div>
              <div className="col-span-2 text-xs text-[#5c7368] bg-[#fef7e0] border border-[#e5cf99] rounded p-2">
                <AlertTriangle size={12} className="inline mr-1 text-[#b08400]" />
                Cambios en <strong>monto total</strong>, <strong>retención</strong> o <strong>NC aplicada</strong> recalculan saldo y estado automáticamente. Si la CxC ya tenía cobros, se respetan.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCxc(null)}>Cancelar</Button>
            <Button onClick={saveEditCxc} className="bg-[#205c45]" data-testid="save-cxc-edit-btn">Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DetalleCuentaDialog
        data={detalleCxc}
        kind="cxc"
        open={!!detalleCxc}
        onClose={() => setDetalleCxc(null)}
        onRefresh={load}
      />
    </div>
  );
}

function CobroPagoDialog({ open, onClose, title, sel, setSel, removeFactura, updateMonto, form, setForm, montoTotal, facturasMismoCliente, addFactura, entityLabel, onSave }) {
  const [ncDisponibles, setNcDisponibles] = useState([]);
  const clienteId = sel[0]?.cxc?.cliente_id;
  const proveedorId = sel[0]?.cxp?.proveedor_id;

  // Cargar NC disponibles (con saldo) del cliente o proveedor según el flujo
  useEffect(() => {
    if (!open || form.metodo !== "nota_credito") { setNcDisponibles([]); return; }
    (async () => {
      try {
        let r;
        if (entityLabel === "cliente" && clienteId) {
          r = await api.get(`/accounting/notas-credito-disponibles?cliente_id=${clienteId}`);
        } else if (entityLabel === "proveedor" && proveedorId) {
          r = await api.get(`/accounting/notas-credito-proveedor-disponibles?proveedor_id=${proveedorId}`);
        } else {
          setNcDisponibles([]); return;
        }
        setNcDisponibles(r.data || []);
      } catch (e) { console.warn("NCs disponibles", e); setNcDisponibles([]); }
    })();
  }, [open, form.metodo, clienteId, proveedorId, entityLabel]);

  if (!open) return null;
  const firstEntity = sel[0]?.cxc?.cliente_nombre || sel[0]?.cxp?.proveedor_nombre || "—";
  const needsBanco = form.metodo === "cheque" || form.metodo === "transferencia";
  const needsCheque = form.metodo === "cheque";
  const needsTransf = form.metodo === "transferencia";
  const needsNC = form.metodo === "nota_credito";
  const ncIdKey = entityLabel === "proveedor" ? "nc_proveedor_id" : "nota_credito_id";
  const ncSel = ncDisponibles.find(n => n.id === form[ncIdKey]);
  const ncSaldo = Number(ncSel?.saldo_disponible || 0);
  const ncExcedido = needsNC && ncSel && montoTotal > ncSaldo + 0.001;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{title} · {firstEntity}</DialogTitle></DialogHeader>

        {/* Facturas aplicadas */}
        <div className="border border-[#d1dbd6] rounded-md overflow-hidden">
          <div className="bg-[#f7f9f8] px-3 py-2 border-b text-xs uppercase tracking-wider font-semibold text-[#5c7368] flex justify-between items-center">
            <span>Facturas aplicadas</span>
            <span className="text-[#205c45] tabular-nums">Total: ${montoTotal.toFixed(2)}</span>
          </div>
          <table className="erp-table">
            <thead><tr><th>Documento</th><th>Fecha / Vence</th><th className="text-right">Saldo</th><th className="text-right w-36">Aplicar</th><th className="w-8"></th></tr></thead>
            <tbody>
              {sel.map(s => {
                const d = s.cxc || s.cxp;
                const factId = d.factura_id || d.factura_compra_id;
                const numDoc = d.numero_documento || d.numero_factura || d.numero || d.id.slice(0, 8);
                return (
                  <tr key={s.id}>
                    <td className="font-mono text-xs">
                      {factId ? <Link to={`/facturacion?f=${factId}`} className="text-[#205c45] hover:underline">{numDoc}</Link> : numDoc}
                    </td>
                    <td className="text-xs">{d.fecha_factura || d.fecha} <span className="text-[#8ba397]">· vence {d.fecha_vencimiento || "—"}</span></td>
                    <td className="text-right tabular-nums text-xs">${d.saldo?.toFixed(2)}</td>
                    <td><Input type="number" step="0.01" className="h-8 text-right tabular-nums" value={s.monto} onChange={(e) => updateMonto(s.id, e.target.value)} data-testid={`monto-${s.id}`} /></td>
                    <td>{sel.length > 1 && <Button size="icon" variant="ghost" onClick={() => removeFactura(s.id)}><span className="text-[#7a2514]">×</span></Button>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {facturasMismoCliente.length > 0 && (
            <div className="px-3 py-2 border-t bg-[#f7f9f8] text-xs">
              <span className="text-[#5c7368] mr-2">+ Agregar del mismo {entityLabel}:</span>
              <Select onValueChange={(v) => addFactura(facturasMismoCliente.find(f => f.id === v))}>
                <SelectTrigger className="h-8 inline-flex w-auto min-w-[280px]"><SelectValue placeholder="Elegir factura pendiente..." /></SelectTrigger>
                <SelectContent>
                  {facturasMismoCliente.map(f => (
                    <SelectItem key={f.id} value={f.id}>
                      {f.numero_documento || f.numero_factura} — vence {f.fecha_vencimiento || "?"} — ${f.saldo?.toFixed(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Forma de pago */}
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div>
            <Label>Método de pago *</Label>
            <Select value={form.metodo} onValueChange={(v) => setForm({ ...form, metodo: v })}>
              <SelectTrigger data-testid="sel-metodo"><SelectValue /></SelectTrigger>
              <SelectContent>{METODOS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Fecha *</Label>
            <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
          </div>

          {needsBanco && (
            <div className="col-span-2">
              <Label>Banco *</Label>
              <Input placeholder="Ej: Banco Pichincha" value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value })} data-testid="field-banco" />
            </div>
          )}
          {needsCheque && (
            <>
              <div><Label>N° Cheque *</Label><Input value={form.numero_cheque} onChange={(e) => setForm({ ...form, numero_cheque: e.target.value })} data-testid="field-cheque" /></div>
              <div><Label>Valor del cheque</Label><Input type="number" step="0.01" value={form.valor_cheque} onChange={(e) => setForm({ ...form, valor_cheque: e.target.value })} /></div>
              <div className="col-span-2"><Label>Titular / Girador</Label><Input value={form.titular_cheque} onChange={(e) => setForm({ ...form, titular_cheque: e.target.value })} /></div>
            </>
          )}
          {needsTransf && (
            <div className="col-span-2"><Label>N° Transacción / Referencia *</Label><Input value={form.numero_transaccion} onChange={(e) => setForm({ ...form, numero_transaccion: e.target.value })} data-testid="field-transaccion" /></div>
          )}
          {needsNC && (
            <div className="col-span-2 border-2 border-dashed border-[#205c45] rounded-md p-3 bg-[#f3f8f5]">
              <Label className="text-[#205c45] font-semibold">Nota de crédito a aplicar *</Label>
              {ncDisponibles.length === 0 ? (
                <div className="text-xs text-[#b53a25] italic mt-1">
                  Este {entityLabel} no tiene notas de crédito con saldo disponible. {entityLabel === "cliente"
                    ? "Genere una primero en Complementos SRI → Notas de crédito."
                    : "Registre primero la NC en Compras → Notas de crédito de proveedor."}
                </div>
              ) : (
                <>
                  <Select value={form[ncIdKey] || ""} onValueChange={(v) => setForm({ ...form, [ncIdKey]: v })}>
                    <SelectTrigger data-testid="field-nc" className="mt-1"><SelectValue placeholder="Elegir nota de crédito…" /></SelectTrigger>
                    <SelectContent>
                      {ncDisponibles.map(nc => (
                        <SelectItem key={nc.id} value={nc.id}>
                          <span className="font-mono">{nc.numero}</span> · {nc.fecha} · disponible ${nc.saldo_disponible?.toFixed(2)} / total ${nc.total?.toFixed(2)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ncSel && (
                    <div className="text-[11px] mt-2 flex items-center gap-3">
                      <span className="text-[#5c7368]">Saldo NC: <strong className="text-[#205c45] tabular-nums">${ncSaldo.toFixed(2)}</strong></span>
                      <span className="text-[#5c7368]">A aplicar: <strong className="tabular-nums">${montoTotal.toFixed(2)}</strong></span>
                      {ncExcedido && <span className="text-[#b53a25] font-semibold">⚠ Excede el saldo de la NC</span>}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <div className="col-span-2 grid grid-cols-2 gap-2">
            <div>
              <Label>N° Recibo (físico)</Label>
              <Input
                value={form.numero_recibo || ""}
                onChange={(e) => setForm({ ...form, numero_recibo: e.target.value })}
                placeholder="Ej: REC-001234"
                data-testid="field-numero-recibo"
              />
            </div>
            <div>
              <Label>Observaciones</Label>
              <Input
                value={form.observaciones || ""}
                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                placeholder="Comentarios adicionales..."
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={onSave} className="bg-[#205c45]" data-testid="save-cobro-btn">
            Registrar · ${montoTotal.toFixed(2)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CxpList() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCxp, setEditingCxp] = useState(null); // CxP siendo editada (dialog admin)
  const [detalleCxp, setDetalleCxp] = useState(null); // Para "Ver detalle" de pagos
  const [sel, setSel] = useState([]);
  const [form, setForm] = useState(emptyPago());

  const openDetalleCxp = async (c) => {
    setDetalleCxp({ loading: true, id: c.id });
    try {
      const { data } = await api.get(`/accounting/cuentas-pagar/${c.id}/detalle`);
      setDetalleCxp(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDetalleCxp(null);
    }
  };

  function emptyPago() {
    return {
      fecha: new Date().toISOString().slice(0, 10),
      metodo: "transferencia",
      banco: "", numero_cheque: "", numero_transaccion: "",
      titular_cheque: "", valor_cheque: "",
      nc_proveedor_id: "",
      numero_recibo: "",
      observaciones: "",
    };
  }

  // Paginación + búsqueda servidor CxP
  const [q, setQ] = useState("");
  const [estado, setEstado] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [filtroFecha, setFiltroFecha] = useState("emision"); // ITER206
  const [sortBy, setSortBy] = useState("fecha_factura");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 20;

  const load = async () => {
    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (estado) params.estado = estado;
    if (q) params.q = q;
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (desde || hasta) params.filtro_fecha = filtroFecha;
    if (sortBy) params.sort_by = sortBy;
    if (sortDir) params.sort_dir = sortDir;
    const r = await api.get("/accounting/cuentas-pagar", { params });
    setItems(r.data || []);
    setTotal(parseInt(r.headers?.["x-total-count"] || r.data?.length || 0, 10));
  };
  useEffect(() => { load().catch(e => toast.error(formatApiError(e))); /* eslint-disable-next-line */ }, [page, estado, q, desde, hasta, filtroFecha, sortBy, sortDir]);
  useEffect(() => { setPage(0); }, [q, estado, desde, hasta, filtroFecha]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };
  const sortIcon = (col) => sortBy !== col ? "" : (sortDir === "asc" ? " ▲" : " ▼");

  const filteredItems = items;
  const pagedItems = items;

  const [facturasProveedor, setFacturasProveedor] = useState([]);
  const openPagar = async (c) => {
    setSel([{ id: c.id, cxp: c, monto: c.saldo.toFixed(2) }]);
    setForm(emptyPago());
    setDialogOpen(true);
    // Cargar TODAS las CxP pendientes del mismo proveedor (cualquier página)
    try {
      const r = await api.get("/accounting/cuentas-pagar", {
        params: { estado: "pendiente", limit: 5000 },
      });
      const todas = (r.data || []).filter(i => i.proveedor_id === c.proveedor_id && i.saldo > 0);
      // Asegurarnos que también incluimos las que pueden estar en estado='parcial'
      const r2 = await api.get("/accounting/cuentas-pagar", {
        params: { estado: "parcial", limit: 5000 },
      });
      const parciales = (r2.data || []).filter(i => i.proveedor_id === c.proveedor_id && i.saldo > 0);
      const all = [...todas, ...parciales];
      // De-duplicar por id
      const seen = new Set();
      setFacturasProveedor(all.filter(x => seen.has(x.id) ? false : (seen.add(x.id), true)));
    } catch (_) { setFacturasProveedor([]); }
  };
  const addFactura = (c) => {
    if (sel.find(s => s.id === c.id)) return;
    setSel(s => [...s, { id: c.id, cxp: c, monto: c.saldo.toFixed(2) }]);
  };
  const removeFactura = (id) => setSel(s => s.filter(x => x.id !== id));
  const updateMonto = (id, v) => setSel(s => s.map(x => x.id === id ? { ...x, monto: v } : x));

  // Acciones admin para CxP: reabrir / editar / eliminar
  const reabrirCxp = async (c) => {
    if (!window.confirm(
      `¿Reabrir la CxP ${c.numero_factura || c.id.slice(0,8)} de ${c.proveedor_nombre}?\n\n` +
      `Monto: $${c.monto_total?.toFixed(2)} · Saldo actual: $${c.saldo?.toFixed(2)} (PAGADA)\n\n` +
      `Si NO tiene pagos reales asociados, volverá a estado PENDIENTE.\n` +
      `Si SÍ tiene pagos, debes anularlos primero desde la pestaña "Pagos".`
    )) return;
    try {
      const { data } = await api.post(`/accounting/cuentas-pagar/${c.id}/reabrir`);
      toast.success(`CxP ${data.numero_factura || ""} reabierta · saldo $${data.saldo_nuevo.toFixed(2)}`);
      load().catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteCxp = async (c) => {
    if (!window.confirm(
      `⚠️ ¿ELIMINAR la CxP ${c.numero_factura || c.id.slice(0,8)} de ${c.proveedor_nombre}?\n\n` +
      `Monto: $${c.monto_total?.toFixed(2)}\n\n` +
      `Esto es PERMANENTE. La CxP no debe tener pagos NO anulados.\n¿Continuar?`
    )) return;
    try {
      await api.delete(`/accounting/cuentas-pagar/${c.id}`);
      toast.success(`CxP ${c.numero_factura || ""} eliminada`);
      load().catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveEditCxp = async () => {
    if (!editingCxp) return;
    try {
      await api.patch(`/accounting/cuentas-pagar/${editingCxp.id}`, {
        numero_factura: editingCxp.numero_factura || null,
        fecha_factura: editingCxp.fecha_factura || null,
        fecha_vencimiento: editingCxp.fecha_vencimiento || null,
        monto_total: Number(editingCxp.monto_total) || 0,
        retencion_aplicada: editingCxp.retencion_aplicada != null ? Number(editingCxp.retencion_aplicada) : null,
        proveedor_nombre: editingCxp.proveedor_nombre || null,
        observaciones: editingCxp.observaciones || null,
      });
      toast.success("CxP actualizada");
      setEditingCxp(null);
      load().catch(() => {});
    } catch (e) { toast.error(formatApiError(e)); }
  };


  const montoTotal = useMemo(() => sel.reduce((t, s) => t + (parseFloat(s.monto) || 0), 0), [sel]);

  const mismoProveedor = useMemo(
    () => sel.length > 0 && sel.every(s => s.cxp.proveedor_id === sel[0].cxp.proveedor_id),
    [sel]
  );

  const facturasMismoProveedor = useMemo(() => {
    if (sel.length === 0) return [];
    const pid = sel[0].cxp.proveedor_id;
    // Usar el catálogo completo cargado al abrir el dialog (todas las páginas)
    const fuente = facturasProveedor.length > 0 ? facturasProveedor : items;
    return fuente.filter(i => i.proveedor_id === pid && i.saldo > 0 && !sel.find(s => s.id === i.id));
  }, [sel, items, facturasProveedor]);

  const pagar = async () => {
    if (sel.length === 0) return toast.error("Agregue al menos una factura");
    if (!mismoProveedor) return toast.error("Todas las facturas deben ser del mismo proveedor");
    if (form.metodo === "cheque" && (!form.banco || !form.numero_cheque)) return toast.error("Cheque requiere banco y número");
    if (form.metodo === "transferencia" && (!form.banco || !form.numero_transaccion)) return toast.error("Transferencia requiere banco y número");
    if (form.metodo === "nota_credito" && !form.nc_proveedor_id) return toast.error("Seleccione la nota de crédito del proveedor a aplicar");
    try {
      await api.post("/accounting/pagos", {
        fecha: form.fecha,
        metodo: form.metodo,
        aplicaciones: sel.map(s => ({ cuenta_por_pagar_id: s.id, monto: parseFloat(s.monto) || 0 })),
        banco: form.banco || null,
        numero_cheque: form.numero_cheque || null,
        numero_transaccion: form.numero_transaccion || null,
        titular_cheque: form.titular_cheque || null,
        valor_cheque: form.valor_cheque ? parseFloat(form.valor_cheque) : null,
        nc_proveedor_id: form.nc_proveedor_id || null,
        numero_recibo: form.numero_recibo || null,
        observaciones: form.observaciones || null,
      });
      toast.success(`Pago registrado · ${sel.length} factura${sel.length > 1 ? "s" : ""} · Total $${montoTotal.toFixed(2)}`);
      setDialogOpen(false); setSel([]); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[260px]">
          <Input placeholder="Buscar proveedor o número factura..." value={q} onChange={(e) => setQ(e.target.value)} className="h-9" data-testid="cxp-search" />
        </div>
        <select value={estado} onChange={(e) => setEstado(e.target.value)} className="h-9 px-2 rounded border border-[#d1dbd6] text-sm bg-white" data-testid="cxp-estado">
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="parcial">Parcial</option>
          <option value="pagada">Pagada</option>
          <option value="vencida">Vencida</option>
        </select>
        <select value={filtroFecha} onChange={(e) => setFiltroFecha(e.target.value)}
          className="h-9 px-2 rounded border border-[#d1dbd6] text-sm bg-white" data-testid="cxp-filtro-fecha"
          title="Sobre qué fecha se aplica el rango Desde/Hasta">
          <option value="emision">Por fecha de emisión</option>
          <option value="vencimiento">Por fecha de vencimiento</option>
        </select>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9 w-36" placeholder="Desde" data-testid="cxp-desde" title="Fecha desde" />
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-36" placeholder="Hasta" data-testid="cxp-hasta" title="Fecha hasta" />
        {(desde || hasta || q || estado) && (
          <Button size="sm" variant="ghost" onClick={() => { setQ(""); setEstado(""); setDesde(""); setHasta(""); setFiltroFecha("emision"); }} className="text-[#b53a25]" data-testid="cxp-clear">Limpiar</Button>
        )}
        <span className="text-xs text-[#5c7368] ml-2 self-center">{total} total{total !== 1 ? "es" : ""}</span>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr>
            <th onClick={() => toggleSort("numero_factura")} className="cursor-pointer select-none">Factura{sortIcon("numero_factura")}</th>
            <th onClick={() => toggleSort("proveedor_nombre")} className="cursor-pointer select-none">Proveedor{sortIcon("proveedor_nombre")}</th>
            <th onClick={() => toggleSort("fecha_factura")} className="cursor-pointer select-none">Fecha{sortIcon("fecha_factura")}</th>
            <th onClick={() => toggleSort("fecha_vencimiento")} className="cursor-pointer select-none">Vencimiento{sortIcon("fecha_vencimiento")}</th>
            <th onClick={() => toggleSort("monto_total")} className="text-right cursor-pointer select-none">Total{sortIcon("monto_total")}</th>
            <th className="text-right">Pagado</th>
            <th onClick={() => toggleSort("saldo")} className="text-right cursor-pointer select-none">Saldo{sortIcon("saldo")}</th>
            <th onClick={() => toggleSort("estado")} className="cursor-pointer select-none">Estado{sortIcon("estado")}</th>
            <th className="w-28"></th>
          </tr></thead>
          <tbody>
            {pagedItems.map(c => (
              <tr key={c.id}>
                <td className="font-mono text-xs">
                  {c.factura_compra_id ? (
                    <Link to={`/compras?f=${c.factura_compra_id}`} className="text-[#205c45] hover:underline inline-flex items-center gap-1">
                      {c.numero_factura} <ExternalLink size={10} />
                    </Link>
                  ) : c.numero_factura}
                </td>
                <td className="font-medium">{c.proveedor_nombre}</td>
                <td>{c.fecha_factura || "—"}</td>
                <td>{c.fecha_vencimiento || "—"}</td>
                <td className="text-right tabular-nums">${c.monto_total?.toFixed(2)}</td>
                <td className="text-right tabular-nums">${c.monto_pagado?.toFixed(2)}</td>
                <td className="text-right tabular-nums font-semibold">${c.saldo?.toFixed(2)}</td>
                <td><Badge variant="outline" className="capitalize">{c.estado}</Badge></td>
                <td>
                  <div className="flex gap-1 justify-end items-center">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openDetalleCxp(c)} title="Ver detalle de pagos" data-testid={`detalle-cxp-${c.id}`}>
                      <Eye size={14} className="text-[#205c45]" />
                    </Button>
                    {c.saldo > 0 && <Button size="sm" className="bg-[#205c45]" onClick={() => openPagar(c)} data-testid={`pagar-${c.id}`}>Pagar</Button>}
                    {isAdmin && c.saldo <= 0.001 && c.estado === "pagada" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-[#b08400] border-[#b08400] hover:bg-[#fef7e0] h-7 px-2"
                        onClick={() => reabrirCxp(c)}
                        data-testid={`reabrir-cxp-${c.id}`}
                        title="Reabrir CxP (solo admin · solo sin pagos reales)"
                      >
                        <Undo2 size={12} className="mr-1" /> Reabrir
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7"
                        onClick={() => setEditingCxp({ ...c })}
                        title="Editar (solo admin)"
                        data-testid={`edit-cxp-${c.id}`}
                      >
                        <FileText size={14} className="text-[#205c45]" />
                      </Button>
                    )}
                    {isAdmin && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-[#b53a25] hover:bg-[#fdecea]"
                        onClick={() => deleteCxp(c)}
                        title="Eliminar (solo admin · solo sin pagos)"
                        data-testid={`delete-cxp-${c.id}`}
                      >
                        <Undo2 size={14} className="rotate-180" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8ba397]">Sin CxP registradas</td></tr>}
          </tbody>
        </table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex justify-center items-center gap-4 text-xs pb-12">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} data-testid="cxp-page-prev">← Anterior</Button>
          <span className="text-[#5c7368]">Página {page + 1} de {Math.ceil(total / PAGE_SIZE)} · Mostrando {page * PAGE_SIZE + 1}–{Math.min(total, (page + 1) * PAGE_SIZE)}</span>
          <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} data-testid="cxp-page-next">Siguiente →</Button>
        </div>
      )}

      <CobroPagoDialog
        open={dialogOpen} onClose={() => setDialogOpen(false)}
        title="Registrar pago"
        sel={sel} setSel={setSel} removeFactura={removeFactura} updateMonto={updateMonto}
        form={form} setForm={setForm} montoTotal={montoTotal}
        facturasMismoCliente={facturasMismoProveedor} addFactura={addFactura}
        entityLabel="proveedor" onSave={pagar}
      />

      {/* Editor admin de CxP */}
      <Dialog open={!!editingCxp} onOpenChange={(v) => { if (!v) setEditingCxp(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar CxP {editingCxp?.numero_factura || ""}</DialogTitle>
          </DialogHeader>
          {editingCxp && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2">
                <Label>Proveedor</Label>
                <Input value={editingCxp.proveedor_nombre || ""} onChange={(e) => setEditingCxp({ ...editingCxp, proveedor_nombre: e.target.value })} />
              </div>
              <div>
                <Label>N° Factura</Label>
                <Input value={editingCxp.numero_factura || ""} onChange={(e) => setEditingCxp({ ...editingCxp, numero_factura: e.target.value })} />
              </div>
              <div>
                <Label>Monto total ($)</Label>
                <Input type="number" step="0.01" value={editingCxp.monto_total ?? 0} onChange={(e) => setEditingCxp({ ...editingCxp, monto_total: e.target.value })} />
              </div>
              <div>
                <Label>Retención aplicada ($)</Label>
                <Input type="number" step="0.01" value={editingCxp.retencion_aplicada ?? 0} onChange={(e) => setEditingCxp({ ...editingCxp, retencion_aplicada: e.target.value })} data-testid="cxp-retencion-input" />
              </div>
              <div>
                <Label>Fecha factura</Label>
                <Input type="date" value={editingCxp.fecha_factura || ""} onChange={(e) => setEditingCxp({ ...editingCxp, fecha_factura: e.target.value })} />
              </div>
              <div>
                <Label>Fecha vencimiento</Label>
                <Input type="date" value={editingCxp.fecha_vencimiento || ""} onChange={(e) => setEditingCxp({ ...editingCxp, fecha_vencimiento: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Observaciones</Label>
                <Textarea rows={2} value={editingCxp.observaciones || ""} onChange={(e) => setEditingCxp({ ...editingCxp, observaciones: e.target.value })} />
              </div>
              <div className="col-span-2 text-xs text-[#5c7368] bg-[#fef7e0] border border-[#e5cf99] rounded p-2">
                <AlertTriangle size={12} className="inline mr-1 text-[#b08400]" />
                Editar el <strong>monto total</strong> o la <strong>retención aplicada</strong> recalcula automáticamente el saldo. Si la CxP ya tiene pagos parciales, el estado se ajusta.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingCxp(null)}>Cancelar</Button>
            <Button onClick={saveEditCxp} className="bg-[#205c45]" data-testid="save-cxp-edit-btn">Guardar cambios</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DetalleCuentaDialog
        data={detalleCxp}
        kind="cxp"
        open={!!detalleCxp}
        onClose={() => setDetalleCxp(null)}
      />
    </div>
  );
}

function Cobros() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const load = () => api.get("/accounting/cobros").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const anular = async (c) => {
    const msg = `¿ANULAR el cobro ${c.numero} de ${c.cliente_nombre || ""} por $${(c.monto_total || 0).toFixed(2)}?\n\nSe revertirán las aplicaciones a las facturas (saldo + monto) y se creará un asiento contrario.\n\nEsta acción NO se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/accounting/cobros/${c.id}/anular`);
      toast.success(`Cobro ${c.numero} anulado · ${data.revertidas?.length || 0} factura(s) reabierta(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const eliminar = async (c) => {
    const msg = `¿ELIMINAR PERMANENTEMENTE el cobro ${c.numero} por $${(c.monto_total || 0).toFixed(2)}?\n\nEl cobro debe estar ANULADO. Esta acción es irreversible y borra el registro para siempre.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/accounting/cobros/${c.id}`);
      toast.success(`Cobro ${c.numero} eliminado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
      <table className="erp-table">
        <thead><tr><th>Número</th><th>Fecha</th><th>Cliente</th><th>Método</th><th>Banco / Referencia</th><th>N° Recibo</th><th>Facturas</th><th className="text-right">Monto</th><th>Obs.</th><th className="w-24 text-right">Acciones</th></tr></thead>
        <tbody>
          {items.map(c => {
            const refTxt = c.metodo === "cheque" ? `Cheque ${c.numero_cheque || "—"}`
              : c.metodo === "transferencia" ? `Transf. ${c.numero_transaccion || "—"}`
              : c.referencia || "—";
            const apls = c.aplicaciones || [];
            const isAnulado = c.estado === "anulado";
            return (
              <tr key={c.id} className={isAnulado ? "opacity-60" : ""} data-testid={`cobro-row-${c.id}`}>
                <td className={`font-mono text-xs ${isAnulado ? "line-through" : ""}`}>{c.numero}</td>
                <td>{c.fecha}</td>
                <td className="font-medium">
                  <div>{c.cliente_nombre}</div>
                  {c.cliente_nombre_comercial && c.cliente_nombre_comercial !== c.cliente_nombre && (
                    <div className="text-[10px] text-[#5c7368] font-normal italic">{c.cliente_nombre_comercial}</div>
                  )}
                </td>
                <td>
                  <Badge variant="outline" className="capitalize">{c.metodo || "—"}</Badge>
                  {isAnulado && <Badge className="ml-1 bg-[#b53a25] text-white text-[10px]">ANULADO</Badge>}
                </td>
                <td className="text-xs">{c.banco ? <><strong>{c.banco}</strong> · {refTxt}</> : refTxt}</td>
                <td className="text-xs font-mono text-[#205c45]">{c.numero_recibo || "—"}</td>
                <td className="text-xs">
                  {apls.length > 0 ? apls.map((a, i) => (
                    <span key={i} className="inline-block mr-1">
                      {a.factura_id ? <Link to={`/facturacion?f=${a.factura_id}`} className="text-[#205c45] hover:underline font-mono">{a.factura_numero || "—"}</Link> : (a.factura_numero || "—")}
                      <span className="text-[#8ba397]">${a.monto?.toFixed(2)}</span>
                      {i < apls.length - 1 && <span className="text-[#c7d3cc]"> · </span>}
                    </span>
                  )) : "—"}
                </td>
                <td className="text-right font-semibold tabular-nums">${(c.monto_total ?? c.monto)?.toFixed(2)}</td>
                <td className="text-xs text-[#5c7368] max-w-[200px] truncate" title={c.observaciones}>{c.observaciones || "—"}</td>
                <td className="text-right">
                  <div className="flex gap-1 justify-end">
                    {!isAnulado && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#b53a25] hover:bg-[#fdecea]" onClick={() => anular(c)} title="Anular cobro" data-testid={`anular-cobro-${c.id}`}>
                        <Undo2 size={14} />
                      </Button>
                    )}
                    {isAdmin && isAnulado && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#b53a25] hover:bg-[#fdecea]" onClick={() => eliminar(c)} title="Eliminar permanentemente (solo admin · debe estar anulado)" data-testid={`delete-cobro-${c.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && <tr><td colSpan={10} className="text-center py-10 text-[#8ba397]">Sin cobros</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Pagos() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const load = () => api.get("/accounting/pagos").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  const anular = async (p) => {
    const msg = `¿ANULAR el pago ${p.numero} a ${p.proveedor_nombre || ""} por $${(p.monto_total || 0).toFixed(2)}?\n\nSe revertirán las aplicaciones a las facturas de compra (saldo + monto) y se creará un asiento contrario.\n\nEsta acción NO se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(`/accounting/pagos/${p.id}/anular`);
      toast.success(`Pago ${p.numero} anulado · ${data.revertidas?.length || 0} factura(s) reabierta(s)`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const eliminar = async (p) => {
    const msg = `¿ELIMINAR PERMANENTEMENTE el pago ${p.numero} por $${(p.monto_total || 0).toFixed(2)}?\n\nEl pago debe estar ANULADO. Esta acción es irreversible y borra el registro para siempre.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/accounting/pagos/${p.id}`);
      toast.success(`Pago ${p.numero} eliminado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
      <table className="erp-table">
        <thead><tr><th>Número</th><th>Fecha</th><th>Proveedor</th><th>Método</th><th>Banco / Referencia</th><th>Facturas</th><th className="text-right">Monto</th><th>Obs.</th><th className="w-24 text-right">Acciones</th></tr></thead>
        <tbody>
          {items.map(c => {
            const refTxt = c.metodo === "cheque" ? `Cheque ${c.numero_cheque || "—"}`
              : c.metodo === "transferencia" ? `Transf. ${c.numero_transaccion || "—"}`
              : c.referencia || "—";
            const apls = c.aplicaciones || [];
            const isAnulado = c.estado === "anulado";
            return (
              <tr key={c.id} className={isAnulado ? "opacity-60" : ""} data-testid={`pago-row-${c.id}`}>
                <td className={`font-mono text-xs ${isAnulado ? "line-through" : ""}`}>{c.numero}</td>
                <td>{c.fecha}</td>
                <td className="font-medium">{c.proveedor_nombre}</td>
                <td>
                  <Badge variant="outline" className="capitalize">{c.metodo || "—"}</Badge>
                  {isAnulado && <Badge className="ml-1 bg-[#b53a25] text-white text-[10px]">ANULADO</Badge>}
                </td>
                <td className="text-xs">{c.banco ? <><strong>{c.banco}</strong> · {refTxt}</> : refTxt}</td>
                <td className="text-xs">
                  {apls.length > 0 ? apls.map((a, i) => (
                    <span key={i} className="inline-block mr-1">
                      {a.factura_compra_id ? <Link to={`/compras?f=${a.factura_compra_id}`} className="text-[#205c45] hover:underline font-mono">{a.factura_numero || "—"}</Link> : (a.factura_numero || "—")}
                      <span className="text-[#8ba397]">${a.monto?.toFixed(2)}</span>
                      {i < apls.length - 1 && <span className="text-[#c7d3cc]"> · </span>}
                    </span>
                  )) : "—"}
                </td>
                <td className="text-right font-semibold tabular-nums">${(c.monto_total ?? c.monto)?.toFixed(2)}</td>
                <td className="text-xs text-[#5c7368] max-w-[200px] truncate" title={c.observaciones}>{c.observaciones || "—"}</td>
                <td className="text-right">
                  <div className="flex gap-1 justify-end">
                    {isAdmin && !isAnulado && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#b53a25] hover:bg-[#fdecea]" onClick={() => anular(c)} title="Anular pago (solo admin)" data-testid={`anular-pago-${c.id}`}>
                        <Undo2 size={14} />
                      </Button>
                    )}
                    {isAdmin && isAnulado && (
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#b53a25] hover:bg-[#fdecea]" onClick={() => eliminar(c)} title="Eliminar permanentemente (solo admin · debe estar anulado)" data-testid={`delete-pago-${c.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8ba397]">Sin pagos</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function PlanCuentas() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ codigo: "", nombre: "", tipo: "activo", nivel: 1, es_auxiliar: false });

  const load = async () => { const r = await api.get("/accounting/cuentas"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/accounting/cuentas", form); toast.success("Cuenta creada"); setDialogOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end"><Button onClick={() => setDialogOpen(true)} className="bg-[#205c45]" data-testid="create-cuenta-btn"><Plus size={16} className="mr-2" />Nueva cuenta</Button></div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Código</th><th>Nombre</th><th>Tipo</th><th>Nivel</th><th>Auxiliar</th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id}>
                <td className="font-mono text-xs font-semibold">{c.codigo}</td>
                <td className="font-medium">{c.nombre}</td>
                <td><Badge variant="outline" className="capitalize">{c.tipo}</Badge></td>
                <td>{c.nivel}</td>
                <td>{c.es_auxiliar ? "✓" : ""}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8ba397]">Sin cuentas. Cree su plan de cuentas.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva cuenta contable</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Código</Label><Input value={form.codigo} onChange={(e) => setForm({...form, codigo: e.target.value})} data-testid="cuenta-codigo" /></div>
            <div><Label>Nivel</Label><Input type="number" value={form.nivel} onChange={(e) => setForm({...form, nivel: parseInt(e.target.value)||1})} /></div>
            <div className="col-span-2"><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} /></div>
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({...form, tipo: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="activo">Activo</SelectItem><SelectItem value="pasivo">Pasivo</SelectItem>
                  <SelectItem value="patrimonio">Patrimonio</SelectItem>
                  <SelectItem value="ingreso">Ingreso</SelectItem><SelectItem value="egreso">Egreso</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={save} className="bg-[#205c45]">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Asientos() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/accounting/asientos").then(r => setItems(r.data)); }, []);
  return (
    <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
      <table className="erp-table">
        <thead><tr><th>Número</th><th>Fecha</th><th>Tipo</th><th>Glosa</th><th className="text-right">Monto</th></tr></thead>
        <tbody>
          {items.map(a => (
            <tr key={a.id}>
              <td className="font-mono text-xs font-semibold">{a.numero}</td>
              <td>{a.fecha}</td>
              <td><Badge variant="outline">{a.tipo}</Badge></td>
              <td>{a.glosa}</td>
              <td className="text-right font-semibold tabular-nums">${a.monto_total?.toFixed(2)}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-[#8ba397]">Sin asientos. Se crean automáticamente al registrar cobros y pagos.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}


export function AgingPanel() {
  const { user } = useAuth();
  const isVentas = user?.role === "ventas";
  const [tipo, setTipo] = useState("cxc");
  const [vendedorId, setVendedorId] = useState("");
  const [vendedoresList, setVendedoresList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [reasignDialog, setReasignDialog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { tipo };
      if (vendedorId && tipo === "cxc") params.vendedor_id = vendedorId;
      const { data } = await api.get("/accounting/aging", { params });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tipo, vendedorId]);

  // Cargar lista de usuarios (vendedores potenciales) — solo si no es ventas
  useEffect(() => {
    if (isVentas) return;
    api.get("/users/basic").then(({ data }) => setVendedoresList(data.filter(u => u.activo))).catch(() => {});
  }, [isVentas]);

  const buckets = data?.buckets || { corriente: 0, "0_30": 0, "31_60": 0, "61_90": 0, mas_90: 0 };
  const counts = data?.counts || {};
  const total = data?.total || 0;
  const pct = (v) => total > 0 ? (v / total * 100).toFixed(1) : "0.0";

  const filtered = useMemo(() => {
    const list = data?.contrapartes || [];
    if (!search.trim()) return list;
    const s = search.toLowerCase();
    return list.filter(c => (c.nombre || "").toLowerCase().includes(s));
  }, [data, search]);

  const exportCSV = () => {
    if (!data) return;
    const header = ["Contraparte","Vendedor","Total","Corriente","0-30","31-60","61-90","+90","# fact","Máx vencido"];
    const rows = (data.contrapartes || []).map(c => [c.nombre, c.vendedor_nombre || "Sin asignar", c.total, c.corriente, c["0_30"], c["31_60"], c["61_90"], c.mas_90, c.n_facturas, c.max_dias_vencido]);
    const csv = [header.join(","), ...rows.map(r => r.map(x => typeof x === "string" ? `"${x.replace(/"/g, '""')}"` : x).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `aging-${tipo}-${data.fecha_corte}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const reasignar = async (clienteId, newVid) => {
    try {
      const { data: res } = await api.patch("/accounting/cxc/asignar-vendedor-cliente", {
        cliente_id: clienteId,
        vendedor_id: newVid || null,
        solo_sin_asignar: true,
      });
      toast.success(`${res.modificadas} CxC re-asignadas`);
      setReasignDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const Bucket = ({ label, value, count, color, idx }) => (
    <div className={`rounded-md border ${color} p-4`} data-testid={`aging-bucket-${idx}`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-1">${(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
      <div className="text-[11px] opacity-70 mt-0.5">{count || 0} facturas · {pct(value)}%</div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="aging-panel">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {!isVentas && (
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="w-44" data-testid="aging-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cxc">Cuentas por Cobrar</SelectItem>
                <SelectItem value="cxp">Cuentas por Pagar</SelectItem>
              </SelectContent>
            </Select>
          )}
          {!isVentas && tipo === "cxc" && (
            <Select value={vendedorId || "all"} onValueChange={(v) => setVendedorId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="aging-vendedor-select"><SelectValue placeholder="Todos los vendedores" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los vendedores</SelectItem>
                <SelectItem value="__sin_asignar__">⚠ Sin asignar</SelectItem>
                {vendedoresList.map(v => <SelectItem key={v.id} value={v.id}>{v.nombres} {v.apellidos}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {isVentas && <span className="text-sm text-[#205c45] font-medium">📂 Mi cartera personal</span>}
          {data && <span className="text-xs text-[#5c7368]">Corte: {data.fecha_corte}</span>}
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data} data-testid="aging-export-btn">
          <Download size={14} className="mr-1" /> Exportar CSV
        </Button>
      </div>

      {loading && <div className="text-sm text-[#5c7368]">Cargando…</div>}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Bucket idx="corriente" label="Al día (corriente)" value={buckets.corriente} count={counts.corriente} color="bg-[#e9f2ee] border-[#b2d0c2] text-[#205c45]" />
            <Bucket idx="0_30" label="1 a 30 días" value={buckets["0_30"]} count={counts["0_30"]} color="bg-[#fef7ec] border-[#f5c477] text-[#8a5500]" />
            <Bucket idx="31_60" label="31 a 60 días" value={buckets["31_60"]} count={counts["31_60"]} color="bg-[#fcf2eb] border-[#e8b693] text-[#9a4920]" />
            <Bucket idx="61_90" label="61 a 90 días" value={buckets["61_90"]} count={counts["61_90"]} color="bg-[#fdecea] border-[#e8a394] text-[#a73a25]" />
            <Bucket idx="mas_90" label="+ 90 días" value={buckets.mas_90} count={counts.mas_90} color="bg-[#fbe1de] border-[#d99584] text-[#b53a25]" />
          </div>

          {total > 0 && (
            <div className="rounded-md overflow-hidden border border-[#d1dbd6] flex h-3" title={`Total $${total.toFixed(2)}`}>
              <div className="bg-[#205c45]" style={{ width: `${pct(buckets.corriente)}%` }} />
              <div className="bg-[#f5c477]" style={{ width: `${pct(buckets["0_30"])}%` }} />
              <div className="bg-[#e8b693]" style={{ width: `${pct(buckets["31_60"])}%` }} />
              <div className="bg-[#e8a394]" style={{ width: `${pct(buckets["61_90"])}%` }} />
              <div className="bg-[#b53a25]" style={{ width: `${pct(buckets.mas_90)}%` }} />
            </div>
          )}

          {!isVentas && tipo === "cxc" && data.vendedores && data.vendedores.length > 0 && !vendedorId && (
            <div className="bg-white rounded-md border border-[#d1dbd6] p-4">
              <h3 className="font-semibold text-[#15241d] mb-3">Cartera por vendedor ({data.vendedores.length})</h3>
              <div className="overflow-x-auto">
                <table className="erp-table text-sm">
                  <thead><tr><th>Vendedor</th><th className="text-right">Total</th><th className="text-right">Al día</th><th className="text-right">1-30</th><th className="text-right">31-60</th><th className="text-right">61-90</th><th className="text-right">+90</th><th className="text-right">Clientes</th><th className="text-right">Facturas</th></tr></thead>
                  <tbody>
                    {data.vendedores.map(v => (
                      <tr key={v.vendedor_id} className={v.vendedor_id === "__sin_asignar__" ? "bg-[#fef7ec]" : ""}>
                        <td className="font-medium">
                          {v.vendedor_id === "__sin_asignar__" ? <span className="text-[#8a5500]">⚠ {v.vendedor_nombre}</span> : v.vendedor_nombre}
                        </td>
                        <td className="text-right tabular-nums font-semibold">${v.total.toLocaleString()}</td>
                        <td className="text-right tabular-nums text-[#205c45]">${v.corriente.toLocaleString()}</td>
                        <td className="text-right tabular-nums text-[#8a5500]">${v["0_30"].toLocaleString()}</td>
                        <td className="text-right tabular-nums text-[#9a4920]">${v["31_60"].toLocaleString()}</td>
                        <td className="text-right tabular-nums text-[#a73a25]">${v["61_90"].toLocaleString()}</td>
                        <td className="text-right tabular-nums text-[#b53a25] font-semibold">${v.mas_90.toLocaleString()}</td>
                        <td className="text-right tabular-nums">{v.n_clientes}</td>
                        <td className="text-right tabular-nums">{v.n_facturas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="bg-white rounded-md border border-[#d1dbd6] p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-[#15241d]">Detalle por {tipo === "cxc" ? "cliente" : "proveedor"} ({data.n_contrapartes})</h3>
              <Input className="max-w-xs" placeholder="Buscar…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="aging-search-input" />
            </div>
            <div className="overflow-x-auto">
              <table className="erp-table text-sm">
                <thead>
                  <tr>
                    <th>{tipo === "cxc" ? "Cliente" : "Proveedor"}</th>
                    {tipo === "cxc" && <th>Vendedor</th>}
                    <th className="text-right">Total</th>
                    <th className="text-right">Corriente</th>
                    <th className="text-right">1-30</th>
                    <th className="text-right">31-60</th>
                    <th className="text-right">61-90</th>
                    <th className="text-right">+90</th>
                    <th className="text-right">N° Fact</th>
                    <th className="text-right">Máx vencido</th>
                    {tipo === "cxc" && !isVentas && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, 200).map((c) => (
                    <tr key={c.id}>
                      <td className="font-medium">{c.nombre}</td>
                      {tipo === "cxc" && <td className="text-xs text-[#4a5c53]">{c.vendedor_nombre || <span className="text-[#8a5500]">⚠ Sin asignar</span>}</td>}
                      <td className="text-right tabular-nums font-semibold">${c.total.toLocaleString()}</td>
                      <td className="text-right tabular-nums text-[#205c45]">${c.corriente.toLocaleString()}</td>
                      <td className="text-right tabular-nums text-[#8a5500]">${c["0_30"].toLocaleString()}</td>
                      <td className="text-right tabular-nums text-[#9a4920]">${c["31_60"].toLocaleString()}</td>
                      <td className="text-right tabular-nums text-[#a73a25]">${c["61_90"].toLocaleString()}</td>
                      <td className="text-right tabular-nums text-[#b53a25] font-semibold">${c.mas_90.toLocaleString()}</td>
                      <td className="text-right tabular-nums">{c.n_facturas}</td>
                      <td className="text-right tabular-nums">
                        {c.max_dias_vencido > 90 && <AlertTriangle size={12} className="inline text-[#b53a25] mr-1" />}
                        {c.max_dias_vencido}d
                      </td>
                      {tipo === "cxc" && !isVentas && (
                        <td>
                          <Button size="sm" variant="ghost" onClick={() => setReasignDialog({ cliente: c })} data-testid={`aging-reasign-${c.id}`}>
                            Asignar
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={tipo==="cxc"?11:9} className="text-center py-8 text-[#8ba397]">Sin saldo pendiente</td></tr>}
                </tbody>
              </table>
              {filtered.length > 200 && (
                <p className="text-xs text-[#5c7368] mt-2 italic">Mostrando 200 de {filtered.length} resultados.</p>
              )}
            </div>
          </div>

          <Dialog open={!!reasignDialog} onOpenChange={(v) => { if (!v) setReasignDialog(null); }}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Asignar vendedor</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-sm">Cliente: <b>{reasignDialog?.cliente?.nombre}</b></p>
                <p className="text-xs text-[#5c7368]">Sólo se modificarán las CxC de este cliente que NO tengan vendedor asignado.</p>
                <Select onValueChange={(v) => reasignar(reasignDialog.cliente.id, v === "none" ? null : v)}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar vendedor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin vendedor —</SelectItem>
                    {vendedoresList.map(u => <SelectItem key={u.id} value={u.id}>{u.nombres} {u.apellidos} ({u.role})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}
